import React, { useState, useRef, Fragment, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { Dispatch } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';

import { DatePicker } from '@atlaskit/datetime-picker';
import ArrowRightIcon from '@atlaskit/icon/core/migration/arrow-right';

import { fg } from '@confluence/feature-gating';

import { DueDateSelectOptions, FilterKeys } from './filtersDefinitions';
import { SelectFilter } from './SelectFilter';
import type { FiltersState } from './useFiltersReducer';

const i18n = defineMessages({
	dueDateLabel: {
		id: 'home.views.tasks.filters.label.dueDate',
		defaultMessage: 'Due Date',
		description: 'The label for a due date selector.',
	},
	optionOverdue: {
		id: 'home.views.tasks.filters.option.overdue',
		defaultMessage: 'Overdue',
		description: 'selectable option to filter for overdue tasks',
	},
	optionOneWeek: {
		id: 'home.views.tasks.filters.option.one.week',
		defaultMessage: 'Within a week',
		description: 'selectable option to filter for tasks due within a week',
	},
	optionTwoWeeks: {
		id: 'home.views.tasks.filters.option.two.weeks',
		defaultMessage: 'Within 2 weeks',
		description: 'selectable option to filter for tasks due within two weeks',
	},
	optionOneMonth: {
		id: 'home.views.tasks.filters.option.one.month',
		defaultMessage: 'Within a month',
		description: 'selectable option to filter for tasks due within a month',
	},
	optionCustom: {
		id: 'home.views.tasks.filters.option.custom',
		defaultMessage: 'Custom',
		description: 'selectable option to filter for tasks in a custom date range',
	},
	optionNoDate: {
		id: 'home.views.tasks.filters.option.no.date',
		defaultMessage: 'No due date',
		description: 'selectable option to filter for tasks without a due date',
	},
	fromLabel: {
		id: 'home.views.tasks.filters.label.from',
		defaultMessage: 'From',
		description: 'label for an input field where the user can pick a starting date to filter',
	},
	toLabel: {
		id: 'home.views.tasks.filters.label.to',
		defaultMessage: 'To',
		description: 'label for an input field where the user can pick an ending date to filter',
	},
});

interface SelectDueDateProps {
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DatePickerContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: 100,
	justifyContent: 'space-between',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		width: '40%',
	},
});

const datePickerContainerStyles = css({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: 100,
	justifyContent: 'space-between',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		width: '40%',
	},
});

export const SelectDueDate = ({ filterDispatch, filterState }: SelectDueDateProps) => {
	const { formatMessage } = useIntl();
	const options = [
		{
			label: formatMessage(i18n.optionOverdue),
			value: DueDateSelectOptions.OVERDUE,
		},
		{
			label: formatMessage(i18n.optionOneWeek),
			value: DueDateSelectOptions.ONE_WEEK,
		},
		{
			label: formatMessage(i18n.optionTwoWeeks),
			value: DueDateSelectOptions.TWO_WEEKS,
		},
		{
			label: formatMessage(i18n.optionOneMonth),
			value: DueDateSelectOptions.ONE_MONTH,
		},
		{
			label: formatMessage(i18n.optionNoDate),
			value: DueDateSelectOptions.NO_DATE,
		},
		{
			label: formatMessage(i18n.optionCustom),
			value: DueDateSelectOptions.CUSTOM,
		},
	];

	const [showDatePicker, setShowDatePicker] = useState(false);
	const dateRangeRef = useRef({
		// @ts-ignore
		from: filterState.filters[FilterKeys.DUE_DATE]?.from,
		// @ts-ignore
		to: filterState.filters[FilterKeys.DUE_DATE]?.to,
	});

	useEffect(() => {
		// @ts-ignore
		const isCustomDateSet = !!filterState.filters[FilterKeys.DUE_DATE]?.range;
		setShowDatePicker(isCustomDateSet);
		if (!isCustomDateSet) {
			dateRangeRef.current = {
				from: null,
				to: null,
			};
		}
	}, [filterState]);

	const onSelection = (props) => {
		if (props.value === 'custom') {
			setShowDatePicker(true);
		} else {
			setShowDatePicker(false);

			filterDispatch({
				type: FilterKeys.DUE_DATE,
				payload: props.value,
			});
		}
	};

	const onDateSelection = (value, field) => {
		if (field === 'from') {
			dateRangeRef.current.from = value;

			filterDispatch({
				type: FilterKeys.DUE_DATE,
				payload: dateRangeRef.current,
			});
		}

		if (field === 'to') {
			dateRangeRef.current.to = value;

			filterDispatch({
				type: FilterKeys.DUE_DATE,
				payload: dateRangeRef.current,
			});
		}
	};

	const getSelectedValue = () => {
		// If the user is picking a custom date we should show the custom option selected
		// @ts-ignore
		if (filterState.filters[FilterKeys.DUE_DATE]?.range || showDatePicker) return options[5];

		return options.find(({ value }) => filterState.filters[FilterKeys.DUE_DATE] === value);
	};

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<Fragment>
				<SelectFilter
					options={options}
					filterKey={FilterKeys.DUE_DATE}
					label={formatMessage(i18n.dueDateLabel)}
					inputId="due-date-filter"
					onChange={onSelection}
					value={getSelectedValue() ?? null}
				/>
				{showDatePicker ? (
					<div css={datePickerContainerStyles}>
						<DatePicker
							onChange={(value) => onDateSelection(value, 'from')}
							defaultValue={dateRangeRef.current.from || undefined}
							hideIcon
							placeholder={formatMessage(i18n.fromLabel)}
						/>
						<ArrowRightIcon label="arrow icon" LEGACY_size="medium" />
						<DatePicker
							onChange={(value) => onDateSelection(value, 'to')}
							defaultValue={dateRangeRef.current.to || undefined}
							hideIcon
							placeholder={formatMessage(i18n.toLabel)}
						/>
					</div>
				) : null}
			</Fragment>
		);
	}

	return (
		<Fragment>
			<SelectFilter
				options={options}
				filterKey={FilterKeys.DUE_DATE}
				label={formatMessage(i18n.dueDateLabel)}
				inputId="due-date-filter"
				onChange={onSelection}
				value={getSelectedValue() ?? null}
			/>
			{showDatePicker ? (
				<DatePickerContainer>
					<DatePicker
						onChange={(value) => onDateSelection(value, 'from')}
						defaultValue={dateRangeRef.current.from || undefined}
						hideIcon
						placeholder={formatMessage(i18n.fromLabel)}
					/>
					<ArrowRightIcon label="arrow icon" LEGACY_size="medium" />
					<DatePicker
						onChange={(value) => onDateSelection(value, 'to')}
						defaultValue={dateRangeRef.current.to || undefined}
						hideIcon
						placeholder={formatMessage(i18n.toLabel)}
					/>
				</DatePickerContainer>
			) : null}
		</Fragment>
	);
};
